import { SelectableItem } from "../processingActivity/ProcessingActivity.types";
import { IdNamePair } from "../customLists/customListView/CustomListView.types.ts";
import { exhaustiveGuard } from "../common/utilities.ts";
import { DateFieldValue } from "../common/components/filterPopover/subMenues/DateFieldSubMenu.tsx";
import { FilterOperatorUi } from "@common/api/filterTypes.ts";

export interface UsersViewModel {
    users: Array<UserViewModel>;
    businessAreas: Array<SelectableItem>;
    userRoles: Array<SelectableItem>;
}

export interface UserRoleItem extends SelectableItem {
    hasFullAccess: boolean;
}

export interface UserViewModel {
    id: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    businessAreas: Array<SelectableItem>;
    userRoles: Array<UserRoleItem>;
    gdpo: boolean;
    isActive: boolean;
    lastLogin: Date | undefined;
    created: Date;
    fullName: string;
    integrationIdentifier: string;
    userGroupEntities: Array<IdNamePair>;
    startPage: UserStartPage;
}

export interface UserRowTableModel extends UserViewModel {
    lastLoginString: string;
    createdString: string;
    isActiveString: string;
}

export interface UserGroupEntities {
    legalEntityId: string;
    name: string;
    address: string;
    zipCode: string;
    city: string;
    country: string;
}

export interface UserSaveModel {
    firstName: string;
    lastName: string;
    emailAddress: string;
    businessAreas: Array<string>;
    userRoles: Array<string>;
    groupEntityIds: Array<string>;
    gdpo: boolean;
    integrationIdentifier: string;
    startPage?: UserStartPage;
}

export enum LoginProvider {
    DotLegalIdp = 1,
    AzureAd = 2,
}

export interface UserSelectableItem {
    name: string;
    id: string;
    active: boolean;
    email: string;
}

export function getLoginProviderName(loginProvider: LoginProvider) {
    switch (loginProvider) {
        case LoginProvider.DotLegalIdp:
            return ".legal IDP";
        case LoginProvider.AzureAd:
            return "Azure AD";
        default:
            throw new Error(`Unknown loginProvider ${loginProvider}`);
    }
}

export enum UserStartPage {
    Dashboard = 1,
    Frameworks = 2,
    ProcessingActivities = 3,
    Record = 4,
    Assets = 5,
    Risk = 6,
    Contracts = 7,
    Tasks = 8,
    AnnualWheelActivity = 9,
    Policies = 10,
    IncidentLog = 11,
    LegalEntityVendors = 12,
    LegalEntityCustomers = 13,
    LegalEntityGroupCompanies = 14,
    LegalEntityOthers = 15,
    ExternalAudits = 16,
    InternalAssessments = 17,
    LegalEntityExternal = 18,
}

export interface SelectableUserRoleItem {
    id: string;
    name: string;
    startPages: Array<UserStartPage>;
    hasFullAccess: boolean;
}

export function getStartPageName(startPage: UserStartPage, translateString: any) {
    switch (startPage) {
        case UserStartPage.Dashboard:
            return translateString("dashboard");
        case UserStartPage.Frameworks:
            return translateString("frameworks");
        case UserStartPage.ProcessingActivities:
            return translateString("processingActivities");
        case UserStartPage.Record:
            return translateString("record");
        case UserStartPage.Assets:
            return translateString("assets");
        case UserStartPage.Risk:
            return translateString("risk");
        case UserStartPage.Contracts:
            return translateString("contracts");
        case UserStartPage.Tasks:
            return translateString("tasks");
        case UserStartPage.AnnualWheelActivity:
            return translateString("annualWheel");
        case UserStartPage.Policies:
            return translateString("policies");
        case UserStartPage.IncidentLog:
            return translateString("incidentLog");
        case UserStartPage.LegalEntityExternal:
            return translateString("external");
        case UserStartPage.LegalEntityVendors:
            return translateString("legalEntityVendors");
        case UserStartPage.LegalEntityCustomers:
            return translateString("legalEntityCustomers");
        case UserStartPage.LegalEntityGroupCompanies:
            return translateString("legalEntityGroupCompanies");
        case UserStartPage.LegalEntityOthers:
            return translateString("legalEntityOthers");
        case UserStartPage.ExternalAudits:
            return translateString("externalAudits");
        case UserStartPage.InternalAssessments:
            return translateString("internalAssessments");
        default:
            exhaustiveGuard(startPage);
    }
}

export interface UsersOverviewFilterModel {
    businessAreaIds: Array<string>;
    roleIds: Array<string>;
    isActive: Array<string>;
}

// Mirrors Backend/Core/Filtering/Users/UserColumns.cs — the shared column vocabulary for saved views and export.
export enum UserColumn {
    Name = "name",
    EmailAddress = "emailAddress",
    BusinessAreas = "businessAreas",
    Roles = "roles",
    GroupEntities = "groupEntities",
    LastLogin = "lastLogin",
    Created = "created",
    Status = "status",
}

export const allUserColumns: Array<UserColumn> = Object.values(UserColumn);

export interface UserExportHeader {
    header: string;
    column: string;
}

// The client-side filter snapshot persisted in saved views. Cleared filters are dropped from the maps (not stored
// as empty values) so the shape deep-equals a saved view's JSON round-trip. Sort lives top-level as
// sortByColumn/sortDirection because the shared change detection in PageView.hooks excludes those keys.
export interface UsersSavedFilter {
    selectFilters: Record<string, Array<string>>;
    dateFilters: Record<string, DateFieldValue>;
    filterOperators: Record<string, FilterOperatorUi>;
    sortByColumn?: string;
    sortDirection?: "asc" | "desc";
}
